import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import isUnknownFieldError from "src/util/isUnknownFieldError";
import {
  personOverlayAttributeKeysFromQueryData,
  type PersonOverlayAttributeKey,
} from "./personOverlayAttributes";
import { personOverlayAttributeKeys as personOverlayAttributeKeysQuery } from "./queries/personOverlayAttributeKeys.graphql";
import { positionOverlayAttributeKeys as positionOverlayAttributeKeysQuery } from "./queries/positionOverlayAttributeKeys.graphql";
import { teamOverlayAttributeKeys as teamOverlayAttributeKeysQuery } from "./queries/teamOverlayAttributeKeys.graphql";

export const overlayKeysFromNamedField = (
  data: unknown,
  field: "positionOverlayAttributeKeys" | "teamOverlayAttributeKeys"
): PersonOverlayAttributeKey[] => {
  if (typeof data !== "object" || data === null) {
    return [];
  }
  if (!(field in data)) {
    return [];
  }
  const keys = data[field];
  if (!Array.isArray(keys)) {
    return [];
  }
  return personOverlayAttributeKeysFromQueryData({
    personOverlayAttributeKeys: keys,
  });
};

export const positionOverlayAttributeKeysFromQueryData = (
  data: unknown
): PersonOverlayAttributeKey[] =>
  overlayKeysFromNamedField(data, "positionOverlayAttributeKeys");

export const teamOverlayAttributeKeysFromQueryData = (
  data: unknown
): PersonOverlayAttributeKey[] =>
  overlayKeysFromNamedField(data, "teamOverlayAttributeKeys");

const schemaSafeOverlayError = (error: unknown): unknown =>
  error && !isUnknownFieldError(error) ? error : undefined;

/**
 * ponytail: stacked deploy — position/team queries use errorPolicy "all"
 * because the API field may not exist yet. Unknown-field errors degrade to
 * people-only (empty position/team catalogs). Real GraphQL errors are not
 * treated as empty catalogs; they fold into the returned error after person.
 */
export const overlayCatalogQueryError = ({
  personError,
  positionError,
  teamError,
}: {
  personError: unknown;
  positionError: unknown;
  teamError: unknown;
}): unknown =>
  personError ??
  schemaSafeOverlayError(positionError) ??
  schemaSafeOverlayError(teamError);

export const useOverlayAttributeCatalogQuery = ({
  skip = false,
  fetchPolicy = "cache-first",
}: {
  skip?: boolean;
  fetchPolicy?: "cache-and-network" | "network-only" | "cache-first";
} = {}): {
  catalog: PersonOverlayAttributeKey[];
  personCatalog: PersonOverlayAttributeKey[];
  positionCatalog: PersonOverlayAttributeKey[];
  teamCatalog: PersonOverlayAttributeKey[];
  loading: boolean;
  error: unknown;
} => {
  // personOverlayAttributeKeys is derived from people_search person data (not a
  // static workspace catalog). cache-first means a newly-imported custom person
  // attribute may stay invisible until full reload; Attribute-Defaults save does
  // not refresh person data. Position/team keys are true workspace catalogs.
  const personQuery = useQuery(personOverlayAttributeKeysQuery, {
    skip,
    fetchPolicy,
  });
  const positionQuery = useQuery(positionOverlayAttributeKeysQuery, {
    skip,
    fetchPolicy,
    errorPolicy: "all",
  });
  const teamQuery = useQuery(teamOverlayAttributeKeysQuery, {
    skip,
    fetchPolicy,
    errorPolicy: "all",
  });

  const personCatalog = useMemo(
    () => personOverlayAttributeKeysFromQueryData(personQuery.data),
    [personQuery.data]
  );
  const positionCatalog = useMemo(
    () => positionOverlayAttributeKeysFromQueryData(positionQuery.data),
    [positionQuery.data]
  );
  const teamCatalog = useMemo(
    () => teamOverlayAttributeKeysFromQueryData(teamQuery.data),
    [teamQuery.data]
  );
  const catalog = useMemo(
    () => [...personCatalog, ...positionCatalog, ...teamCatalog],
    [personCatalog, positionCatalog, teamCatalog]
  );

  return {
    catalog,
    personCatalog,
    positionCatalog,
    teamCatalog,
    loading: Boolean(
      personQuery.loading || positionQuery.loading || teamQuery.loading
    ),
    error: overlayCatalogQueryError({
      personError: personQuery.error,
      positionError: positionQuery.error,
      teamError: teamQuery.error,
    }),
  };
};
